// Plik: frontend/src/pages/RegistrationForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { 
  Container, Typography, Box, Paper, Grid, 
  TextField, Button, FormControl, RadioGroup, 
  FormControlLabel, Radio, Checkbox, FormGroup,
  FormHelperText, CircularProgress, Divider, 
  Accordion, AccordionSummary, AccordionDetails,
  Alert, Link as MuiLink
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { registrationService, settingsService } from '../services/api';
import toast from 'react-hot-toast';

// Schemat walidacji formularza
const RegistrationSchema = Yup.object().shape({
  schoolName: Yup.string()
    .required('Nazwa klienta jest wymagana'),
  schoolAddress: Yup.string()
    .required('Adres klienta jest wymagany'),
  fullName: Yup.string()
    .required('Imię i nazwisko są wymagane'),
  email: Yup.string()
    .email('Nieprawidłowy format adresu email')
    .required('Adres email jest wymagany'),
  phone: Yup.string()
    .required('Numer telefonu jest wymagany')
    .matches(/^[0-9]{9}$/, 'Numer telefonu powinien składać się z 9 cyfr'),
  previousVisit: Yup.string()
    .required('Wybierz jedną z opcji'),
  slotId: Yup.number()
    .required('Wybierz termin wizyty')
    .positive('Wybierz termin wizyty'),
  termsAccepted: Yup.boolean()
    .oneOf([true], 'Musisz zaakceptować regulamin'),
  contactConsent: Yup.boolean()
    .required('To pole jest wymagane'),
  marketingConsent: Yup.boolean()
    .notRequired()
});

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [settings, setSettings] = useState(null);
  
  // Pobierz dostępne terminy i ustawienia
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Pobierz ustawienia
      const settingsResponse = await settingsService.getSettings();
      if (settingsResponse.success) {
        setSettings(settingsResponse.data);
      }
      
      // Pobierz sloty
      const response = await registrationService.getSlots();
      
      if (response.success) {
        // Przygotowanie odpowiedniej struktury danych
        // Przekształcamy dane z API do odpowiedniego formatu
        const slotsGroupedByDate = {};
        
        // Grupujemy terminy według daty
        response.data.forEach(slot => {
          if (!slotsGroupedByDate[slot.date]) {
            slotsGroupedByDate[slot.date] = [];
          }
          
          slotsGroupedByDate[slot.date].push({
            id: slot.id,
            time: slot.time,
            available_spots: slot.capacity - slot.registered
          });
        });
        
        // Ustawiamy zgrupowane terminy
        setSlots(slotsGroupedByDate);
        
        // Ustaw pierwszą dostępną datę jako wybraną
        const dates = Object.keys(slotsGroupedByDate);
        if (dates.length > 0) {
          setSelectedDate(dates[0]);
        }
      } else {
        setError(response.error);
        toast.error('Nie udało się pobrać dostępnych terminów');
      }
      
      setLoading(false);
    };
    
    fetchData();
  }, []);
  
  // Obsługa przesłania formularza
  const handleSubmit = async (values, { setSubmitting, resetForm, setErrors }) => {
    const response = await registrationService.registerParticipant(values);
    
    if (response.success) {
      toast.success('Rejestracja zakończona pomyślnie!');
      resetForm();
      navigate('/dziekujemy');
    } else {
      if (response.error === 'Ten adres email jest już zarejestrowany') {
        setErrors({ email: response.error });
      } else if (response.error === 'Brak dostępnych miejsc w wybranym terminie') {
        setErrors({ slotId: response.error });
        // Odśwież dostępne terminy
        const refreshResponse = await registrationService.getSlots();
        if (refreshResponse.success) {
          setSlots(refreshResponse.data);
        }
      } else {
        toast.error(response.error || 'Wystąpił błąd podczas rejestracji');
      }
    }
    
    setSubmitting(false);
  };
  
  // Renderowanie opcji terminów dla wybranej daty
  const renderTimeSlots = (values, setFieldValue, errors, touched) => {
    // Sprawdzamy, czy selectedDate istnieje i czy slots[selectedDate] jest tablicą
    if (!selectedDate || !slots[selectedDate] || !Array.isArray(slots[selectedDate]) || slots[selectedDate].length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Brak dostępnych terminów dla wybranej daty
        </Typography>
      );
    }
    
    return (
      <FormControl component="fieldset" error={errors.slotId && touched.slotId}>
        <RadioGroup
          aria-label="time-slot"
          name="slotId"
          value={values.slotId || ''}
          onChange={(e) => setFieldValue('slotId', Number(e.target.value))}
        >
          <Grid container spacing={2}>
            {slots[selectedDate].map((slot) => (
              <Grid item xs={12} sm={6} md={4} key={slot.id}>
                <Paper
                  elevation={values.slotId === slot.id ? 3 : 1}
                  sx={{
                    p: 2,
                    border: values.slotId === slot.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    opacity: slot.available_spots <= 0 ? 0.6 : 1
                  }}
                >
                  <FormControlLabel
                    value={slot.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1">{slot.time}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {slot.available_spots > 0 
                            ? `Dostępnych miejsc: ${slot.available_spots}` 
                            : 'Brak miejsc'}
                        </Typography>
                      </Box>
                    }
                    disabled={slot.available_spots <= 0}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
        {errors.slotId && touched.slotId && (
          <FormHelperText error>{errors.slotId}</FormHelperText>
        )}
      </FormControl>
    );
  };
  
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }
  
  // Funkcja do renderowania dat w zakładkach
  const renderDateTabs = () => {
    return Object.keys(slots).map((date) => (
      <Button
        key={date}
        variant={selectedDate === date ? "contained" : "outlined"}
        onClick={() => setSelectedDate(date)}
        sx={{ mx: 1, mb: 2 }}
      >
        {new Date(date).toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}
      </Button>
    ));
  };

  // Tekst labela regulaminu z linkiem do pliku
  const renderTermsLabel = () => {
    const termsText = settings?.terms_label || 'Akceptuję regulamin wydarzenia';
    const regulaminUrl = settings?.regulamin_url;
    
    if (regulaminUrl) {
      return (
        <span>
          {termsText.replace('regulamin', '').replace('wydarzenia', '').trim()}{' '}
          <MuiLink 
            href={regulaminUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ fontWeight: 'bold' }}
          >
            regulamin wydarzenia
          </MuiLink>
        </span>
      );
    }
    
    return termsText;
  };
  
  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {settings?.registration_title || 'Rejestracja na Wydarzenie'}
        </Typography>
        
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Formik
            initialValues={{
              schoolName: '',
              schoolAddress: '',
              fullName: '',
              email: '',
              phone: '',
              previousVisit: '',
              slotId: '',
              termsAccepted: false,
              contactConsent: false,
              marketingConsent: false
            }}
            validationSchema={RegistrationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              isSubmitting
            }) => (
              <Form>
                <Grid container spacing={3}>
                  {/* Wybór terminu */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Wybierz termin wizyty
                    </Typography>
                    
                    <Box sx={{ mb: 2, overflowX: 'auto', pb: 1 }}>
                      <Box sx={{ display: 'flex' }}>
                        {renderDateTabs()}
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      {renderTimeSlots(values, setFieldValue, errors, touched)}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  {/* Dane biura */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Dane placówki
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="schoolName"
                      label="Nazwa placówki"
                      fullWidth
                      variant="outlined"
                      error={touched.schoolName && Boolean(errors.schoolName)}
                      helperText={touched.schoolName && errors.schoolName}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="schoolAddress"
                      label="Adres placówki"
                      fullWidth
                      variant="outlined"
                      error={touched.schoolAddress && Boolean(errors.schoolAddress)}
                      helperText={touched.schoolAddress && errors.schoolAddress}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  {/* Dane osobowe */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Dane osobowe
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="fullName"
                      label="Imię i nazwisko"
                      fullWidth
                      variant="outlined"
                      error={touched.fullName && Boolean(errors.fullName)}
                      helperText={touched.fullName && errors.fullName}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="email"
                      label="Adres e-mail"
                      fullWidth
                      variant="outlined"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="phone"
                      label="Numer telefonu"
                      fullWidth
                      variant="outlined"
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  {/* Dodatkowe informacje */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Dodatkowe informacje
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      Czy odwiedzałeś/aś nas wcześniej?
                    </Typography>
                    
                    <FormControl component="fieldset" error={touched.previousVisit && Boolean(errors.previousVisit)}>
                      <RadioGroup
                        name="previousVisit"
                        value={values.previousVisit}
                        onChange={handleChange}
                      >
                        <FormControlLabel value="nigdy" control={<Radio />} label="Nigdy" />
                        <FormControlLabel value="rok_temu" control={<Radio />} label="Rok temu" />
                        <FormControlLabel value="dwa_lata_temu" control={<Radio />} label="Dwa lata temu" />
                        <FormControlLabel value="trzy_lub_wiecej_lat_temu" control={<Radio />} label="Trzy lub więcej lat temu" />
                      </RadioGroup>
                      {touched.previousVisit && errors.previousVisit && (
                        <FormHelperText error>{errors.previousVisit}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  
                  {/* Zgody */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Wymagane zgody
                    </Typography>
                    
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="termsAccepted"
                            checked={values.termsAccepted}
                            onChange={handleChange}
                          />
                        }
                        label={renderTermsLabel()}
                      />
                      {touched.termsAccepted && errors.termsAccepted && (
                        <FormHelperText error>{errors.termsAccepted}</FormHelperText>
                      )}
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="contactConsent"
                            checked={values.contactConsent}
                            onChange={handleChange}
                          />
                        }
                        label={settings?.contact_consent_label || 'Wyrażam zgodę na kontakt w celu przekazania informacji o wydarzeniu'}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="marketingConsent"
                            checked={values.marketingConsent}
                            onChange={handleChange}
                          />
                        }
                        label={settings?.marketing_consent_label || 'Wyrażam zgodę na otrzymywanie informacji marketingowych i ofert handlowych'}
                      />
                    </FormGroup>
                  </Grid>
                  
                  {/* Link do regulaminu na dole */}
                  {settings?.regulamin_url && (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <MuiLink
                          href={settings.regulamin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          📄 Pobierz regulamin wydarzenia
                        </MuiLink>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Przyciski formularza */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isSubmitting}
                      fullWidth
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'Zapisz się'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
        
        <Box sx={{ my: 3, textAlign: 'center' }}>
          <Button variant="text" color="primary" onClick={() => navigate('/')}>
            Powrót do strony głównej
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default RegistrationForm;